import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../api/client';
import { Plus, X, Loader2, Calendar, Phone, MapPin, Sparkles, CheckCircle2, Trash2, ChevronLeft, ChevronRight, Clock, AlertOctagon, CalendarClock, CheckSquare, Square } from 'lucide-react';
import CsvImport from '../components/app/CsvImport';
import SearchBar, { matches } from '../components/app/SearchBar';
import { useSyncListener } from '../hooks/useDataSync';

const TYPE_LABELS = {
  visita_presencial: '🚶 Visita presencial',
  llamada: '📞 Llamada',
  seguimiento: '👀 Seguimiento',
  recordatorio: '⏰ Recordatorio',
};
const CAT = {
  L1: { label: 'L1 · Conflictivo (3 visitas/año)', color: 'bg-red-500/20 text-red-300 border-red-500/40', dot: 'bg-red-500' },
  L2: { label: 'L2 · Incidencia (2 visitas/año)', color: 'bg-orange-500/20 text-orange-300 border-orange-500/40', dot: 'bg-orange-500' },
  L3: { label: 'L3 · Normal (1 visita/año)', color: 'bg-green-500/20 text-green-300 border-green-500/40', dot: 'bg-green-500' },
};
const STATUS_LABEL = { pendiente: 'Pendiente', completada: 'Completada', pospuesta: 'Pospuesta', cancelada: 'Cancelada' };

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const iso = (d) => d.toISOString().slice(0, 10);
const parseISO = (s) => new Date(s + 'T12:00:00');

const Agenda = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date());
  const [view, setView] = useState('list'); // list | calendar
  const [modal, setModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, st, u] = await Promise.all([
        api.get('/agenda'),
        api.get('/sites/lite'),
        api.get('/agenda/stats').catch(() => ({ data: null })),
        api.get('/admin/users').catch(() => ({ data: [] })),
      ]);
      setItems(a.data);
      setSites(s.data);
      setUsers(u.data);
      if (st.data) setStats(st.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useSyncListener(load, ['agenda', 'sites']);

  const siteMap = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const autoGenerate = async () => {
    if (!confirm(`¿Generar calendario ${new Date().getFullYear()}?\nAsigna 3/2/1 visitas + llamadas según categoría (Conflictivo/Incidencia/Normal).`)) return;
    setGenerating(true); setToast(null);
    try {
      const year = new Date().getFullYear();
      const { data } = await api.post(`/agenda/auto-generate?year=${year}`);
      setToast({ type: 'ok', msg: `✅ Generadas ${data.created} tareas · L1=${data.by_category.L1} · L2=${data.by_category.L2} · L3=${data.by_category.L3}` });
      await load();
    } catch (e) { setToast({ type: 'err', msg: 'Error: ' + (e.response?.data?.detail || e.message) }); }
    finally { setGenerating(false); }
  };

  const complete = async (t) => {
    await api.put(`/agenda/${t.id}`, {
      site_id: t.site_id, type: t.type, scheduled_date: t.scheduled_date, category: t.category,
      hunter_id: t.hunter_id, status: 'completada', title: t.title, notes: t.notes,
      auto_generated: t.auto_generated, year: t.year,
    });
    await load();
  };

  const del = async (id) => {
    if (!confirm('¿Eliminar tarea?')) return;
    await api.delete(`/agenda/${id}`);
    await load();
  };

  const [postponeTarget, setPostponeTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === filtered.length ? new Set() : new Set(filtered.map((t) => t.id))));
  };
  const exitSelection = () => { setSelectionMode(false); setSelectedIds(new Set()); };
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`¿Eliminar ${selectedIds.size} tarea(s) de la agenda? No se puede deshacer.`)) return;
    setDeletingBulk(true);
    try {
      await api.post('/agenda/bulk-delete', { ids: Array.from(selectedIds) });
      exitSelection();
      await load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setDeletingBulk(false); }
  };
  const bulkDeleteAllFiltered = async () => {
    if (!confirm(`⚠️ Vas a eliminar TODAS las tareas visibles (${filtered.length}). Acción irreversible. ¿Continuar?`)) return;
    if (!confirm('Última confirmación: ¿estás seguro?')) return;
    setDeletingBulk(true);
    try {
      // Envía la lista de IDs filtrados como source-of-truth (el usuario ha aplicado búsqueda)
      const { data } = await api.post('/agenda/bulk-delete', { ids: filtered.map((t) => t.id) });
      exitSelection();
      await load();
      alert(`${data.deleted} tarea(s) eliminadas.`);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setDeletingBulk(false); }
  };

  const today = iso(new Date());
  const filtered = useMemo(() => items.filter((t) => {
    const s = siteMap[t.site_id];
    return matches(search, s?.name, s?.municipality, s?.address, t.title, t.notes, TYPE_LABELS[t.type], t.postpone_reason);
  }), [items, siteMap, search]);

  // Calendar grid days
  const cal = useMemo(() => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    const first = new Date(start);
    first.setDate(first.getDate() - ((first.getDay() + 6) % 7)); // Monday start
    const days = [];
    for (let d = new Date(first); d <= end || days.length % 7 !== 0; d.setDate(d.getDate() + 1)) {
      const dISO = iso(d);
      const dayItems = filtered.filter((i) => i.scheduled_date === dISO);
      days.push({ date: new Date(d), iso: dISO, items: dayItems, inMonth: d.getMonth() === month.getMonth() });
      if (days.length > 42) break;
    }
    return days;
  }, [month, filtered]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="agenda-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12]">SEGUIMIENTO</div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1">Agenda del hunter</h1>
          <p className="text-white/50 text-sm mt-1">Visitas y llamadas anuales · categoría según Mantenimiento.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <CsvImport entity="agenda" onImported={load}/>
          <button onClick={autoGenerate} disabled={generating} data-testid="btn-auto-agenda"
            className="flex items-center gap-2 border border-[#A3FF12]/40 text-[#A3FF12] hover:bg-[#A3FF12]/10 px-3 py-2 rounded-xl text-sm font-bold disabled:opacity-50">
            {generating ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>}
            Generar calendario {new Date().getFullYear()}
          </button>
          <button onClick={() => setSelectionMode((v) => !v)} data-testid="btn-toggle-selection"
            className={`flex items-center gap-2 border px-3 py-2 rounded-xl text-sm font-bold ${selectionMode ? 'bg-red-500/15 border-red-400/60 text-red-200' : 'border-white/15 text-white/70 hover:bg-white/5'}`}>
            {selectionMode ? <CheckSquare size={14}/> : <Square size={14}/>}
            {selectionMode ? 'Salir selección' : 'Seleccionar'}
          </button>
          <button onClick={() => setModal(true)} data-testid="btn-new-agenda"
            className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl text-sm hover:bg-[#8ee00a]">
            <Plus size={16}/> Nueva
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase text-white/40">Total</div>
            <div className="text-2xl font-black text-white">{stats.total}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-yellow-500/30 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase text-yellow-300">Pendientes</div>
            <div className="text-2xl font-black text-yellow-400">{stats.pending}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-red-500/30 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase text-red-300">Vencidas</div>
            <div className="text-2xl font-black text-red-400">{stats.overdue}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#A3FF12]/30 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase text-[#A3FF12]">Completadas</div>
            <div className="text-2xl font-black text-[#A3FF12]">{stats.completed}</div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`mb-4 rounded-xl px-4 py-3 text-sm border ${toast.type === 'ok' ? 'bg-[#A3FF12]/10 border-[#A3FF12]/30 text-[#A3FF12]' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <div className="flex bg-black/40 border border-white/10 rounded-lg overflow-hidden">
          <button onClick={() => setView('list')} className={`px-3 py-1.5 text-xs font-bold ${view==='list'?'bg-[#A3FF12] text-black':'text-white/60'}`}>Lista</button>
          <button onClick={() => setView('calendar')} className={`px-3 py-1.5 text-xs font-bold ${view==='calendar'?'bg-[#A3FF12] text-black':'text-white/60'}`}>Calendario</button>
        </div>
        {view === 'calendar' && (
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-1.5 text-white/60 hover:text-[#A3FF12]"><ChevronLeft size={16}/></button>
            <div className="text-white font-bold text-sm min-w-[10ch] text-center">{month.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}</div>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-1.5 text-white/60 hover:text-[#A3FF12]"><ChevronRight size={16}/></button>
          </div>
        )}
      </div>
      <div className="mb-4"><SearchBar value={search} onChange={setSearch} placeholder="Buscar por emplazamiento, municipio o motivo…" testId="agenda-search"/></div>

      {selectionMode && (
        <div data-testid="bulk-actions-bar"
          className="sticky top-2 z-30 mb-3 bg-red-500/15 border border-red-400/50 rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap backdrop-blur">
          <button onClick={toggleSelectAll} data-testid="btn-select-all-visible"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
            {selectedIds.size === filtered.length && filtered.length > 0
              ? <><CheckSquare size={13}/> Deseleccionar todas</>
              : <><Square size={13}/> Seleccionar todas ({filtered.length})</>}
          </button>
          <span className="text-red-100 text-xs font-black">
            {selectedIds.size} seleccionada{selectedIds.size === 1 ? '' : 's'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button onClick={bulkDelete} disabled={deletingBulk} data-testid="btn-bulk-delete"
                className="flex items-center gap-1 bg-red-500 hover:bg-red-400 text-white text-xs font-black px-3 py-1.5 rounded-lg disabled:opacity-40">
                {deletingBulk ? <Loader2 className="animate-spin" size={12}/> : <Trash2 size={12}/>}
                Eliminar {selectedIds.size}
              </button>
            )}
            <button onClick={bulkDeleteAllFiltered} disabled={deletingBulk || filtered.length === 0}
              data-testid="btn-bulk-delete-all"
              className="flex items-center gap-1 bg-red-700 hover:bg-red-600 text-white text-[11px] font-black px-3 py-1.5 rounded-lg disabled:opacity-40"
              title="Borra todas las tareas visibles (respeta la búsqueda)">
              <Trash2 size={12}/> Borrar TODAS ({filtered.length})
            </button>
            <button onClick={exitSelection} className="text-white/70 hover:text-white text-xs font-bold px-2">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl">
          <Calendar className="mx-auto text-white/20 mb-2" size={40}/>
          <p className="text-white/50 mb-2">Sin tareas todavía.</p>
          <p className="text-white/40 text-sm">Pulsa <strong className="text-[#A3FF12]">"Generar calendario"</strong> para autogenerar visitas y llamadas.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40 text-sm border border-dashed border-white/10 rounded-2xl">Ninguna tarea coincide con «{search}».</div>
      ) : view === 'list' ? (
        <div className="space-y-2">
          {filtered.slice(0, 200).map((t) => {
            const overdue = t.status === 'pendiente' && t.scheduled_date < today;
            const isPostponed = t.status === 'pospuesta';
            const site = siteMap[t.site_id];
            return (
              <div key={t.id} data-testid={`agenda-${t.id}`}
                onClick={() => { if (selectionMode) toggleSelect(t.id); }}
                className={`bg-black/30 border rounded-xl p-3 flex items-center gap-3 hover:border-[#A3FF12]/30 ${
                  selectionMode && selectedIds.has(t.id) ? 'border-red-400/70 ring-2 ring-red-400/40 cursor-pointer' :
                  overdue ? 'border-red-500/30' : isPostponed ? 'border-orange-500/30' : 'border-white/10'
                } ${selectionMode ? 'cursor-pointer' : ''}`}>
                {selectionMode && (
                  <input type="checkbox" checked={selectedIds.has(t.id)} readOnly
                    data-testid={`chk-agenda-${t.id}`}
                    className="w-4 h-4 accent-red-400 flex-shrink-0"/>
                )}
                <div className={`w-1.5 h-12 rounded-full ${CAT[t.category]?.dot || 'bg-white/20'}`}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${CAT[t.category]?.color}`}>{t.category}</span>
                    <span className="text-xs text-white/70">{TYPE_LABELS[t.type]}</span>
                    {overdue && <span className="text-[10px] font-bold text-red-400 flex items-center gap-1"><AlertOctagon size={10}/> VENCIDA</span>}
                    {isPostponed && <span className="text-[10px] font-bold text-orange-400 flex items-center gap-1" data-testid={`badge-postponed-${t.id}`}><CalendarClock size={10}/> POSPUESTA</span>}
                    {t.status === 'completada' && <span className="text-[10px] font-bold text-[#A3FF12] flex items-center gap-1"><CheckCircle2 size={10}/> HECHA</span>}
                  </div>
                  <div className="text-white font-bold truncate">{site?.name || 'Site'}</div>
                  <div className="text-white/50 text-xs mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Clock size={11}/> {t.scheduled_date}</span>
                    {t.original_date && t.original_date !== t.scheduled_date && (
                      <span className="text-orange-400/80 line-through">antes: {t.original_date}</span>
                    )}
                    {site?.municipality && <span className="flex items-center gap-1"><MapPin size={11}/> {site.municipality}</span>}
                    {t.hunter_id && userMap[t.hunter_id] && <span>👤 {userMap[t.hunter_id].name}</span>}
                  </div>
                  {isPostponed && t.postpone_reason && (
                    <div className="mt-1.5 text-[11px] text-orange-300/80 bg-orange-500/5 border border-orange-500/20 rounded-lg px-2 py-1">
                      Motivo: <span className="text-orange-200">{t.postpone_reason}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 items-center flex-shrink-0">
                  {(t.status === 'pendiente' || t.status === 'pospuesta') && (
                    <>
                      <button onClick={() => setPostponeTarget(t)} data-testid={`btn-postpone-${t.id}`}
                        className="text-xs border border-orange-400/40 text-orange-300 hover:bg-orange-500/10 font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1">
                        <CalendarClock size={12}/> Posponer
                      </button>
                      <button onClick={() => complete(t)} data-testid={`btn-complete-${t.id}`}
                        className="text-xs bg-[#A3FF12] text-black font-bold px-3 py-1.5 rounded-lg">✓ Hecho</button>
                    </>
                  )}
                  <button onClick={() => del(t.id)} className="text-red-400 hover:text-red-300 p-1.5"><Trash2 size={14}/></button>
                </div>
              </div>
            );
          })}
          {filtered.length > 200 && <p className="text-white/40 text-xs text-center">Mostrando 200 de {filtered.length}. Usa vista calendario o filtros.</p>}
        </div>
      ) : (
        <div className="bg-black/30 border border-white/10 rounded-2xl p-2">
          <div className="grid grid-cols-7 gap-1 mb-2 text-center text-[10px] uppercase text-white/40 font-bold">
            {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cal.map((day) => (
              <div key={day.iso}
                className={`min-h-[70px] rounded-lg p-1.5 text-[10px] ${day.inMonth ? 'bg-black/40 border border-white/5' : 'bg-black/20 opacity-40'} ${day.iso === today ? 'border-[#A3FF12]/50 ring-1 ring-[#A3FF12]/30' : ''}`}>
                <div className={`font-bold text-white/60 ${day.iso === today ? 'text-[#A3FF12]' : ''}`}>{day.date.getDate()}</div>
                <div className="space-y-0.5 mt-1">
                  {day.items.slice(0, 3).map((i) => (
                    <div key={i.id} onClick={() => setDetail(i)} title={siteMap[i.site_id]?.name}
                      className={`truncate px-1 py-0.5 rounded cursor-pointer ${
                        i.status === 'completada' ? 'bg-white/5 line-through text-white/30'
                        : i.status === 'pospuesta' ? 'bg-orange-500/20 text-orange-200 border border-orange-500/40'
                        : CAT[i.category]?.color || 'bg-white/10'
                      }`}>
                      {i.status === 'pospuesta' ? '⏳' : i.type === 'llamada' ? '📞' : '🚶'} {siteMap[i.site_id]?.name?.slice(0, 12) || '—'}
                    </div>
                  ))}
                  {day.items.length > 3 && <div className="text-white/40 text-[9px] px-1">+{day.items.length - 3}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && <AgendaModal sites={sites} users={users} onClose={() => setModal(false)} onSaved={async () => { setModal(false); await load(); }}/>}
      {detail && <DetailModal task={detail} site={siteMap[detail.site_id]} onClose={() => setDetail(null)} onChanged={async () => { setDetail(null); await load(); }} onPostpone={(t) => { setDetail(null); setPostponeTarget(t); }}/>}
      {postponeTarget && <PostponeModal task={postponeTarget} onClose={() => setPostponeTarget(null)} onSaved={async () => { setPostponeTarget(null); await load(); }}/>}
    </div>
  );
};

const AgendaModal = ({ sites, users, onClose, onSaved }) => {
  const [form, setForm] = useState({
    site_id: sites[0]?.id || '',
    type: 'visita_presencial',
    scheduled_date: new Date().toISOString().slice(0, 10),
    category: 'L3',
    hunter_id: '',
    status: 'pendiente',
    title: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/agenda', form); onSaved(); }
    catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="agenda-modal"
        className="bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-white/10 flex justify-between">
          <h3 className="text-lg font-black">Nueva tarea</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs uppercase text-white/60">Emplazamiento</span>
            <select required value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
              <option value="">Selecciona...</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Tipo</span>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Categoría</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                {Object.entries(CAT).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Fecha</span>
              <input type="date" required value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Hunter</span>
              <select value={form.hunter_id} onChange={(e) => setForm({ ...form, hunter_id: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                <option value="">Sin asignar</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase text-white/60">Notas</span>
            <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
          </label>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 rounded-xl text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="btn-save-agenda" className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl disabled:opacity-50">{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </form>
    </div>
  );
};

const DetailModal = ({ task, site, onClose, onChanged, onPostpone }) => {
  const complete = async () => {
    await api.put(`/agenda/${task.id}`, {
      site_id: task.site_id, type: task.type, scheduled_date: task.scheduled_date, category: task.category,
      hunter_id: task.hunter_id, status: 'completada', title: task.title, notes: task.notes,
      auto_generated: task.auto_generated, year: task.year,
    });
    onChanged();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md">
        <div className="p-5 border-b border-white/10 flex justify-between">
          <div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${CAT[task.category]?.color}`}>{task.category}</span>
            <h3 className="text-lg font-black mt-2">{TYPE_LABELS[task.type]}</h3>
            <p className="text-white/50 text-sm">{site?.name} · {task.scheduled_date}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-2">
          {site?.municipality && <div className="text-sm text-white/60"><MapPin size={12} className="inline"/> {site.municipality}</div>}
          {task.notes && <div className="bg-black/30 border border-white/5 rounded-lg p-3 text-sm text-white/70 whitespace-pre-line">{task.notes}</div>}
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-2 flex-wrap">
          {(task.status === 'pendiente' || task.status === 'pospuesta') && onPostpone && (
            <button onClick={() => onPostpone(task)} data-testid="btn-postpone-detail"
              className="border border-orange-400/40 text-orange-300 hover:bg-orange-500/10 font-bold px-4 py-2 rounded-xl text-sm flex items-center gap-1">
              <CalendarClock size={14}/> Posponer
            </button>
          )}
          {(task.status === 'pendiente' || task.status === 'pospuesta') && (
            <button onClick={complete} className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl text-sm">✓ Marcar como completada</button>
          )}
        </div>
      </div>
    </div>
  );
};

const POSTPONE_PRESETS = [
  'Propietario no disponible',
  'Meteorología adversa',
  'Bloqueo de acceso',
  'Cambio de prioridad',
  'Otro',
];

const PostponeModal = ({ task, onClose, onSaved }) => {
  const [newDate, setNewDate] = useState(() => {
    // default = current scheduled + 7 days
    const base = new Date(task.scheduled_date + 'T12:00:00');
    base.setDate(base.getDate() + 7);
    return base.toISOString().slice(0, 10);
  });
  const [preset, setPreset] = useState(POSTPONE_PRESETS[0]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const finalReason = preset === 'Otro' ? reason.trim() : `${preset}${reason.trim() ? ' — ' + reason.trim() : ''}`;
    if (!finalReason) { setErr('El motivo es obligatorio'); return; }
    if (newDate <= task.scheduled_date) { setErr('La nueva fecha debe ser posterior a la actual'); return; }
    setSaving(true); setErr('');
    try {
      await api.post(`/agenda/${task.id}/postpone`, { new_date: newDate, reason: finalReason });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Error al posponer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="postpone-modal"
        className="bg-[#0a0a0a] border border-orange-500/30 rounded-t-2xl sm:rounded-2xl w-full max-w-md">
        <div className="p-5 border-b border-white/10 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 text-orange-300 text-xs font-black tracking-widest"><CalendarClock size={14}/> REPROGRAMAR VISITA</div>
            <h3 className="text-lg font-black text-white mt-1">Nueva fecha para esta tarea</h3>
            <p className="text-white/50 text-xs mt-1">Actual: <span className="text-orange-300">{task.scheduled_date}</span></p>
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs uppercase text-white/60">Nueva fecha</span>
            <input type="date" required value={newDate} min={task.scheduled_date}
              onChange={(e) => setNewDate(e.target.value)} data-testid="postpone-new-date"
              className="w-full mt-1 bg-black/40 border border-white/10 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400"/>
          </label>
          <div>
            <span className="text-xs uppercase text-white/60">Motivo</span>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {POSTPONE_PRESETS.map((p) => (
                <button type="button" key={p} onClick={() => setPreset(p)} data-testid={`postpone-preset-${p.split(' ')[0].toLowerCase()}`}
                  className={`text-xs px-3 py-2 rounded-lg font-bold border transition-colors ${
                    preset === p ? 'bg-orange-500/15 text-orange-300 border-orange-500/40' : 'bg-white/5 text-white/60 border-white/10 hover:text-white'
                  }`}>{p}</button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-xs uppercase text-white/60">
              {preset === 'Otro' ? 'Explica el motivo' : 'Detalle adicional (opcional)'}
            </span>
            <textarea rows="2" required={preset === 'Otro'} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={preset === 'Otro' ? 'Motivo específico...' : 'Cualquier nota extra...'}
              data-testid="postpone-reason-input"
              className="w-full mt-1 bg-black/40 border border-white/10 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400"/>
          </label>
          {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-3 py-2">{err}</div>}
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 rounded-xl text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="btn-confirm-postpone"
            className="bg-orange-400 text-black font-black px-4 py-2 rounded-xl disabled:opacity-50 text-sm inline-flex items-center gap-1">
            {saving ? <><Loader2 size={14} className="animate-spin"/> Guardando…</> : <><CalendarClock size={14}/> Posponer</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Agenda;
